import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { IdGen, Pane, Label, NumericInput, Checkbox, Popup, Text, DateTimeRangePicker, TabPane, TextInput } from '@leverege/ui-elements';
import { Colorizers } from '@leverege/ui-attributes';
import I18N from '@leverege/i18n';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { LogicCondition } from '@leverege/condition';
import Symbolizers from "../../shared/Symbolizers.js";
import CapSelector from "../../shared/views/CapSelector.js";
import JoinSelector from "../../shared/views/JoinSelector.js";
import LineStyleSelector from "../../shared/views/LineStyleSelector.js";
import { HistoryAccumulators } from "../../shared/renderers/accumulator/index.js";
import Model from "../../shared/renderers/HistoryModel.js";
import S from './Grid.css';
import DataViewerCardModel from "../../../dashboard/DataViewerCardModel.js";
import ConditionFilterModels from "../../../../filters/filter/ConditionFilterModels.js";
import HistoryConditionEditor from "../../../history/editors/HistoryConditionEditor.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.map.HistoryEditor');
function HistoryEditor(props) {
  const {
    value,
    delegate,
    dataViewerCardModel
  } = props;
  const onModelChange = useValueChange(Model, props);
  const [ids] = useState(IdGen.createIds('attr', 15));
  let cid = 0;
  const scalesWithZoom = value ? Model.getScalesWithZoom(value) : null;
  const objectType = value ? Model.getObjectType(value) : null;
  const range = value ? Model.getRange(value) : null;
  const timeValue = useMemo(() => {
    if (typeof range === 'string') {
      return range;
    }
    const ret = {
      ...range
    };
    if (ret.start != null && !(ret.start instanceof Date)) {
      ret.start = new Date(ret.start);
    }
    if (ret.end != null && !(ret.end instanceof Date)) {
      ret.end = new Date(ret.end);
    }
    return ret;
  }, [range]);
  const condition = useMemo(() => {
    return Model.getCondition(value) || ConditionFilterModels.getModelFor(delegate.type, objectType)?.create({
      rootCondition: LogicCondition.create({
        operator: 'and'
      })
    }) || null;
  }, [value, delegate.type, objectType]);
  const needsDate = dataViewerCardModel && DataViewerCardModel.getNeedsDate(dataViewerCardModel);
  const needsCondition = dataViewerCardModel && DataViewerCardModel.getNeedsCondition(dataViewerCardModel);
  if (value == null) {
    return null;
  }
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "visibility",
      tabName: t('visibility'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          containerClassName: S.fill,
          label: t('visible'),
          eventData: "setVisible",
          value: Model.getVisible(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('zoom')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.sub, S.grid),
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('showZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            eventData: "setShowAtZoom",
            min: 0,
            max: Math.min(25, Model.getHideAtZoom(value)),
            allowError: true,
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          }, `haz-${Model.getHideAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('hideZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: Math.max(0, Model.getShowAtZoom(value)),
            max: 25,
            eventData: "setHideAtZoom",
            allowError: true,
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          }, `saz-${Model.getShowAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('fadeInOutZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 10,
            eventData: "setFadeZoom",
            allowError: true,
            float: true,
            value: Model.getFadeZoom(value),
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsx(Popup, {
          title: t('help'),
          icon: tIcon('heplIcon', 'fa fa-question-circle fa-fw'),
          children: /*#__PURE__*/_jsxs("div", {
            className: S.pad,
            children: [/*#__PURE__*/_jsx(Text, {
              className: classnames(S.fill, S.hint),
              variant: "caption",
              children: t('settingZoomDescription')
            }), /*#__PURE__*/_jsx(Text, {
              className: S.fill,
              variant: "subtitle",
              children: t('zoomLevel')
            }), /*#__PURE__*/_jsxs("div", {
              className: S.grid,
              style: {
                columnGap: 'var( --size-padXLarge, 32px)'
              },
              children: [/*#__PURE__*/_jsx("span", {
                children: "0"
              }), /*#__PURE__*/_jsx("span", {
                children: t('0Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "3"
              }), /*#__PURE__*/_jsx("span", {
                children: t('3Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "4"
              }), /*#__PURE__*/_jsx("span", {
                children: t('4Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "6"
              }), /*#__PURE__*/_jsx("span", {
                children: t('6Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "10"
              }), /*#__PURE__*/_jsx("span", {
                children: t('10Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "15"
              }), /*#__PURE__*/_jsx("span", {
                children: t('15Zoom')
              }), /*#__PURE__*/_jsx("span", {
                children: "18"
              }), /*#__PURE__*/_jsx("span", {
                children: t('18Zoom')
              })]
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "appearance",
      tabName: t('appearance'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('lineStyle')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('width')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 50,
            float: true,
            eventData: "setLineWidth",
            allowError: true,
            value: Model.getLineWidth(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('color')
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              ...props,
              id: ids[cid++]
            },
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getLineColor(value),
            eventData: 'setLineColor',
            onChange: onModelChange
          }, S.fill), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('opacity')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 1,
            float: true,
            eventData: "setLineOpacity",
            allowError: true,
            value: Model.getLineOpacity(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('style')
          }), /*#__PURE__*/_jsx(LineStyleSelector, {
            id: ids[cid++],
            value: Model.getLineStyle(value),
            eventData: "setLineStyle",
            onChange: onModelChange
          }), Model.getLineStyle(value) === 'pattern' && /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: t('pattern')
            }), /*#__PURE__*/_jsx(TextInput, {
              id: ids[cid++],
              eventData: "setLinePattern",
              allowError: true,
              value: Model.getLinePattern(value),
              onChange: onModelChange
            })]
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('cap')
          }), /*#__PURE__*/_jsx(CapSelector, {
            eventData: "setLineCap",
            value: Model.getLineCap(value) || 'butt',
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('join')
          }), /*#__PURE__*/_jsx(JoinSelector, {
            eventData: "setLineJoin",
            value: Model.getLineJoin(value) || 'miter',
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('symbolizer')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('symbol')
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              id: ids[cid++],
              ...props,
              objectType
            },
            modelKey: 'symbolizer',
            values: Symbolizers.getSymbolizersFor(objectType),
            value: Model.getSymbolizer(value),
            eventData: 'setSymbolizer',
            onChange: onModelChange
          }, S.fill)]
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('scale')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('normal')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setNormalScale",
            value: Model.getNormalScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('rollover')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setRolloverScale",
            value: Model.getRolloverScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('selected')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setSelectedScale",
            value: Model.getSelectedScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('alert')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setAlertScale",
            value: Model.getAlertScale(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Checkbox, {
            id: ids[cid++],
            containerClassName: S.fill,
            label: "Scales with Zoom",
            eventData: "setScalesWithZoom",
            value: scalesWithZoom,
            onChange: onModelChange
          }), scalesWithZoom && /*#__PURE__*/_jsxs("div", {
            className: classnames(S.grid, S.fill, S.sub),
            children: [/*#__PURE__*/_jsxs("div", {
              className: S.grid,
              children: [/*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: t('fromZoom')
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0.1,
                max: 10,
                float: true,
                eventData: "setScaleFromZoom",
                value: Model.getScaleFromZoom(value),
                onChange: onModelChange
              }), /*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: t('toZoom')
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0.1,
                max: 10,
                float: true,
                eventData: "setScaleToZoom",
                value: Model.getScaleToZoom(value),
                onChange: onModelChange
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: S.grid,
              children: [/*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: t('factor')
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0,
                max: 10,
                float: true,
                eventData: "setScaleFromFactor",
                value: Model.getScaleFromFactor(value),
                onChange: onModelChange
              }), /*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: t('factor')
              }), /*#__PURE__*/_jsx(NumericInput, {
                id: ids[cid++],
                min: 0,
                max: 10,
                float: true,
                eventData: "setScaleToFactor",
                value: Model.getScaleToFactor(value),
                onChange: onModelChange
              })]
            })]
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "rollover",
      tabName: t('rollover'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('lineStyle')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('width')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 50,
            float: true,
            eventData: "setRolloverLineWidth",
            allowError: true,
            value: Model.getRolloverLineWidth(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('color')
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              ...props,
              id: ids[cid++]
            },
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getRolloverLineColor(value),
            eventData: 'setRolloverLineColor',
            onChange: onModelChange
          }, S.fill), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('opacity')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 1,
            float: true,
            eventData: "setRolloverLineOpacity",
            allowError: true,
            value: Model.getRolloverLineOpacity(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('style')
          }), /*#__PURE__*/_jsx(LineStyleSelector, {
            id: ids[cid++],
            value: Model.getRolloverLineStyle(value),
            eventData: "setRolloverLineStyle",
            onChange: onModelChange
          }), Model.getRolloverLineStyle(value) === 'pattern' && /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: t('pattern')
            }), /*#__PURE__*/_jsx(TextInput, {
              id: ids[cid++],
              eventData: "setRolloverLinePattern",
              allowError: true,
              value: Model.getRolloverLinePattern(value),
              onChange: onModelChange
            })]
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('cap')
          }), /*#__PURE__*/_jsx(CapSelector, {
            eventData: "setRolloverLineCap",
            value: Model.getRolloverLineCap(value) || 'butt',
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('join')
          }), /*#__PURE__*/_jsx(JoinSelector, {
            eventData: "setRolloverLineJoin",
            value: Model.getRolloverLineJoin(value) || 'miter',
            onChange: onModelChange
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "selected",
      tabName: t('selected'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('lineStyle')
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.grid, S.fill, S.sub),
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('width')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 50,
            float: true,
            eventData: "setSelectedLineWidth",
            allowError: true,
            value: Model.getSelectedLineWidth(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('color')
          }), ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              ...props,
              id: ids[cid++]
            },
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getSelectedLineColor(value),
            eventData: 'setSelectedLineColor',
            onChange: onModelChange
          }, S.fill), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('opacity')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 1,
            float: true,
            eventData: "setSelectedLineOpacity",
            allowError: true,
            value: Model.getSelectedLineOpacity(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('style')
          }), /*#__PURE__*/_jsx(LineStyleSelector, {
            id: ids[cid++],
            value: Model.getSelectedLineStyle(value),
            eventData: "setSelectedLineStyle",
            onChange: onModelChange
          }), Model.getSelectedLineStyle(value) === 'pattern' && /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: t('pattern')
            }), /*#__PURE__*/_jsx(TextInput, {
              id: ids[cid++],
              eventData: "setSelectedLinePattern",
              allowError: true,
              value: Model.getSelectedLinePattern(value),
              onChange: onModelChange
            })]
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('cap')
          }), /*#__PURE__*/_jsx(CapSelector, {
            eventData: "setSelectedLineCap",
            value: Model.getSelectedLineCap(value) || 'butt',
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('join')
          }), /*#__PURE__*/_jsx(JoinSelector, {
            eventData: "setSelectedLineJoin",
            value: Model.getSelectedLineJoin(value) || 'miter',
            onChange: onModelChange
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "interaction",
      tabName: t('interaction'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          containerClassName: S.fill,
          label: t('rollover'),
          eventData: "setRolloverable",
          value: Model.getRolloverable(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Checkbox, {
          id: ids[cid++],
          containerClassName: S.fill,
          label: t('selectable'),
          eventData: "setSelectable",
          value: Model.getSelectable(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.sub, S.grid),
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('startInteractionAtZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            eventData: "setStartInteractionAtZoom",
            min: 0,
            max: Math.min(25, Model.getStartInteractionAtZoom(value)),
            allowError: true,
            value: Model.getStartInteractionAtZoom(value),
            onChange: onModelChange
          }, `stiaz-${Model.getStopInteractionAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('stopInteractionAtZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: Math.max(0, Model.getStartInteractionAtZoom(value)),
            max: 25,
            eventData: "setStopInteractionAtZoom",
            allowError: true,
            value: Model.getStopInteractionAtZoom(value),
            onChange: onModelChange
          }, `spiaz-${Model.getStartInteractionAtZoom(value) || 'n'}`)]
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "query",
      tabName: t('query'),
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor",
        className: S.grid,
        children: [!needsDate && /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(Label, {
            htmlFor: ids[cid],
            children: t('range')
          }), /*#__PURE__*/_jsx(DateTimeRangePicker, {
            id: ids[cid++],
            eventData: "setRange",
            value: timeValue,
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsx(Label, {
          htmlFor: ids[cid],
          children: t('maxPoints')
        }), /*#__PURE__*/_jsx(NumericInput, {
          id: ids[cid++],
          eventData: "setPerPage",
          value: Model.getPerPage(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          children: t('accumulator')
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            id: ids[cid++]
          },
          modelKey: 'accumulator',
          values: HistoryAccumulators.getAccumulatorsFor(objectType),
          value: Model.getAccumulator(value),
          eventData: 'setAccumulator',
          onChange: onModelChange
        }, S.fill), !needsCondition && /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(Label, {
            children: t('condition')
          }), /*#__PURE__*/_jsx(HistoryConditionEditor, {
            ...props,
            objectType: objectType,
            eventData: "setCondition",
            value: condition,
            onChange: onModelChange
          })]
        })]
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(HistoryEditor);